import useProjects from '../../composable/project';
import AlertService from '../../services/errors.service';
import ApiService from '../../services/api.service';
import { useCache } from '../../composable/useCashe';

const { getProjects } = useProjects();
const projectListCache = new Map();

const normalizeProjectQuery = (query = {}) => {
  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  return {
    search: query?.search ?? '',
    status: query?.status ?? '',
    page: toNumber(query?.page, 1),
    perPage: toNumber(query?.perPage ?? query?.per_page, 8)
  };
};

const cacheKeyForQuery = (query) => {
  const params = new URLSearchParams();

  Object.entries(query)
    .sort(([a], [b]) => (a > b ? 1 : -1))
    .forEach(([key, value]) => params.append(key, value ?? ''));

  return `projects:${params.toString()}`;
};

const getProjectListCache = (query) => {
  const normalizedQuery = normalizeProjectQuery(query);
  const cacheKey = cacheKeyForQuery(normalizedQuery);

  if (!projectListCache.has(cacheKey)) {
    const cache = useCache(cacheKey, async () => {
      const resp = await getProjects(normalizedQuery);
      return resp.data;
    });

    projectListCache.set(cacheKey, cache);
  }

  return { cache: projectListCache.get(cacheKey), normalizedQuery };
};

const clearProjectCaches = () => {
  projectListCache.forEach((cache) => cache.clear());
  projectListCache.clear();
};

export const state = {
  projects: [],
  project: [],
  groups: [],
  isProjectListLoading: false
};

export const mutations = {
  SET_LOADING(state, newValue) {
    state.isProjectListLoading = newValue;
  },
  SET_PROJECTS(state, newValue) {
    state.projects = newValue;
  },
  SET_GROUPS(state, newValue) {
    state.groups = newValue;
  },
  deleteItem(state, id) {
    const index = state.projects.data.findIndex((item) => item.id === id);

    state.projects.data.splice(index, 1);
  }
};

export const getters = {
  getProjects(state) {
    return state.projects;
  },
  getGroups(state) {
    return state.groups;
  },
  isLoading(state) {
    return state.isProjectListLoading;
  }
};

export const actions = {
  async getProjectsData({ commit, state }, payload = {}) {
    const { query, forceRefresh = false } = payload?.query ? payload : { query: payload, forceRefresh: false };
    const { cache } = getProjectListCache(query || {});

    const cacheState = typeof cache.getState === 'function' ? cache.getState() : {};
    const hasCachedData = cacheState.data !== null;
    const hasLocalData = Array.isArray(state.projects)
      ? state.projects.length > 0
      : Array.isArray(state.projects?.data)
        ? state.projects.data.length > 0
        : false;
    const hasStorageCache =
      typeof window !== 'undefined' &&
      (() => {
        try {
          return Object.keys(window.localStorage).some((key) => key.startsWith('cache:projects:'));
        } catch {
          return false;
        }
      })();
    if (hasCachedData) {
      // show cached data immediately while we decide whether to refresh
      commit('SET_PROJECTS', cacheState.data);
    }

    const shouldShowLoader = forceRefresh ? !(hasCachedData || hasLocalData) : !(hasCachedData || hasLocalData || hasStorageCache);
    if (shouldShowLoader) commit('SET_LOADING', true);

    try {
      const backgroundRefreshAllowed = hasCachedData && !forceRefresh;
      // Use cache when available; if expired, useCache will refresh internally.
      const result = forceRefresh ? await cache.refresh() : await cache.get({ revalidate: false });

      commit('SET_PROJECTS', result);

      // If we only served cached data, kick off a silent refresh to keep it fresh.
      if (backgroundRefreshAllowed && result === cacheState.data) {
        cache
          .refresh()
          .then((fresh) => commit('SET_PROJECTS', fresh))
          .catch(() => undefined);
      }

      return result;
    } catch (error) {
      AlertService.errorMessage(error);
      throw error;
    } finally {
      if (shouldShowLoader) commit('SET_LOADING', false);
    }
  },

  async getGroups({ commit }, id) {
    const response = await ApiService.get('/groups/' + id);
    if (response.status === 200) {
      const data = response.data.data;
      commit('SET_GROUPS', data);
    }
  },

  async deleteProject({ commit }, id) {
    try {
      const response = await ApiService.delete('/project/' + id);

      if (response.status === 200) {
        AlertService.successMessage(response.data.message);
        clearProjectCaches();
        commit('deleteItem', id);
      }
    } catch (error) {
      AlertService.errorMessage(error);
    }
  },

  invalidateCache() {
    clearProjectCaches();
  }
};
